import { hasOwnProperty } from "@glideapps/ts-necessities";
import * as React from "react";

export type PointerPosition = { x: number; y: number };

type PointerTrackingResult = [
    PointerPosition | undefined,
    {
        onMouseDown: (ev: React.MouseEvent) => void;
        onMouseLeave: (ev: React.MouseEvent) => void;
        onMouseMove: (ev: React.MouseEvent) => void;
        onMouseUp: (ev: React.MouseEvent) => void;
    },
    {
        onTouchStart: (this: HTMLElement, ev: TouchEvent) => void;
        onTouchCancel: (this: HTMLElement, ev: TouchEvent) => void;
        onTouchMove: (this: HTMLElement, ev: TouchEvent) => void;
        onTouchEnd: (this: HTMLElement, ev: TouchEvent) => void;
    }
];

export function hookPointerTouchEventsOnElement(h: PointerTrackingResult[2], e: HTMLElement) {
    const { onTouchStart, onTouchCancel, onTouchMove, onTouchEnd } = h;
    e.addEventListener("touchstart", onTouchStart, { capture: false });
    e.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
    e.addEventListener("touchcancel", onTouchCancel, { capture: true });
    e.addEventListener("touchend", onTouchEnd, { capture: true });
    return () => {
        e.removeEventListener("touchstart", onTouchStart);
        e.removeEventListener("touchmove", onTouchMove);
        e.removeEventListener("touchcancel", onTouchCancel);
        e.removeEventListener("touchend", onTouchEnd);
    };
}

type PointerTransitionHandlers = Readonly<{
    handleMouseDown?: (pos: PointerPosition, ev: React.MouseEvent | TouchEvent) => void;
    handleMouseUp?: (pos: PointerPosition, ev: React.MouseEvent | TouchEvent) => void;
}>;

function isMouseEvent(ev: React.MouseEvent | TouchEvent): ev is React.MouseEvent {
    return hasOwnProperty(ev, "clientX");
}

function cursorPositionFromEvent(ev: React.MouseEvent | TouchEvent): PointerPosition {
    if (isMouseEvent(ev)) {
        return { x: ev.clientX, y: ev.clientY };
    }
    if (ev.type === "touchend") {
        return { x: ev.changedTouches[0].clientX, y: ev.changedTouches[0].clientY };
    }
    return { x: ev.targetTouches[0].clientX, y: ev.targetTouches[0].clientY };
}

export function usePointerTracking(transitionHandlers: PointerTransitionHandlers): PointerTrackingResult {
    const [trackingEnabled, setTrackingEnabled] = React.useState(false);
    const [mousePos, setMousePos] = React.useState<PointerPosition | undefined>();

    const { handleMouseDown, handleMouseUp } = transitionHandlers;

    const onMouseDown = React.useCallback(
        (ev: React.MouseEvent | TouchEvent) => {
            setTrackingEnabled(true);
            const pos = cursorPositionFromEvent(ev);
            if (handleMouseDown !== undefined) {
                handleMouseDown(pos, ev);
            }

            setMousePos(pos);
        },
        [setTrackingEnabled, setMousePos, handleMouseDown]
    );
    const onMouseLeave = React.useCallback(() => {
        setTrackingEnabled(false);
        setMousePos(undefined);
    }, [setTrackingEnabled, setMousePos]);
    const onMouseMove = React.useCallback(
        (ev: React.MouseEvent | TouchEvent) => {
            if (!trackingEnabled) return;

            ev.stopPropagation();
            ev.preventDefault();

            setMousePos(cursorPositionFromEvent(ev));
        },
        [trackingEnabled, setMousePos]
    );
    const onMouseUp = React.useCallback(
        (ev: React.MouseEvent | TouchEvent) => {
            if (!trackingEnabled) return;

            if (handleMouseUp !== undefined) {
                handleMouseUp(cursorPositionFromEvent(ev), ev);
            }
            setTrackingEnabled(false);
            setMousePos(undefined);
        },
        [trackingEnabled, setTrackingEnabled, handleMouseUp]
    );

    const mouseHandlers = React.useMemo(
        () => ({
            onMouseDown,
            onMouseLeave,
            onMouseMove,
            onMouseUp,
        }),
        [onMouseDown, onMouseLeave, onMouseMove, onMouseUp]
    );

    const touchHandlers = React.useMemo(
        () => ({
            onTouchStart: onMouseDown,
            onTouchMove: onMouseMove,
            onTouchCancel: onMouseLeave,
            onTouchEnd: onMouseUp,
        }),
        [onMouseDown, onMouseLeave, onMouseMove, onMouseUp]
    );

    return [mousePos, mouseHandlers, touchHandlers];
}
